import SearchIcon from "@mui/icons-material/Search";
import { Box, InputAdornment, TextField } from "@mui/material";
import { FilterPaper } from "@/components/page/FilterPaper";
import { useEffect, useRef } from "react";
import {
	type SearchShiftsParameters,
	ShiftDayNavigatorControl,
	ShiftPeriodNavigatorControl,
	TreePickerControlV2,
} from "@dexteel/mesf-core";
import {
	AssetsFilter,
	useAssets,
	type Asset,
	type TreeCode,
} from "@/pages/logbook/_shared";
import type { NavigationMode } from "../../hooks/useLogbookEntries";
import { NavigationModeSwitch } from "./NavigationModeSwitch";

const customPeriodOptions = [
	{ value: "Day", label: "Day" },
	{ value: "4Days", label: "4 Days" },
	{ value: "Week", label: "Week" },
	{ value: "Month", label: "Month" },
	{ value: "Custom", label: "Custom" },
	{ value: "All", label: "All" },
];

// The two mesf-core navigators size themselves differently (the period one pads its
// own grid, the shift one uses dense field margins). Without this the card changes
// height on every mode switch and the grid below jumps.
const NAVIGATOR_ROW_MIN_HEIGHT = 61;
// Both navigators need about 420px; below that they move to their own line
// instead of overflowing the card
const NAVIGATOR_MIN_WIDTH = 430;
const navigatorBox = (visible: boolean) => ({
	flex: 1,
	minWidth: NAVIGATOR_MIN_WIDTH,
	// each navigator reserves the same height, so the card keeps its size whether
	// the navigator sits beside the switch or wraps under it
	minHeight: NAVIGATOR_ROW_MIN_HEIGHT,
	display: visible ? "flex" : "none",
	alignItems: "center",
	"& > *": { flex: 1, minWidth: 0 },
	// same field height in both modes
	"& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
	"& .MuiOutlinedInput-root": { height: 37 },
	// keep the shift navigator on one line: its last button has no size and wraps
	"& .MuiGrid2-container": { flexWrap: "nowrap" },
});

const buildTreeAssets = (nodes: any) => {
	if (nodes === undefined) return;

	const idMapping = nodes.reduce((acc: any, el: any, i: number) => {
		acc[el?.id] = i;
		return acc;
	}, {});

	let root;

	nodes.forEach((el: any) => {
		if (el?.parentId === null) {
			root = el;
			return;
		}
		const parentEl = nodes[idMapping[el.parentId]];
		if (parentEl) {
			parentEl.children = [...(parentEl?.children || []), el];
		}
	});

	return root as any;
};

type Props = {
	mode: NavigationMode;
	onModeChange: (mode: NavigationMode) => void;
	withAssetFilter?: "tree" | "selector";
	filterAssets?: "area";
	quickFilter: string;
	onQuickFilterChange: (text: string) => void;
	assetId: number | null;
	setAssetId: (value: number | null) => void;
	shiftPeriod: SearchShiftsParameters;
	onChangeShiftPeriod: (
		value: SearchShiftsParameters,
		period: string | null,
	) => void;
	actualPeriod?: string | null;
	// changing it remounts the period navigator, the only way to move it from outside
	periodNavigatorKey: number;
};

export const Filters = ({
	mode,
	onModeChange,
	withAssetFilter,
	filterAssets,
	quickFilter,
	onQuickFilterChange,
	assetId,
	setAssetId,
	shiftPeriod,
	onChangeShiftPeriod,
	actualPeriod,
	periodNavigatorKey,
}: Props) => {
	const { data } = useAssets();

	// "All" lists every entry; put the cursor in the search so it can be narrowed right away
	const searchRef = useRef<HTMLInputElement>(null);
	const isAll = mode === "period" && actualPeriod === "All";
	useEffect(() => {
		if (isAll) searchRef.current?.focus();
	}, [isAll]);

	const rowsMapped = (Array.isArray(data) ? data : [])
		.filter((a) => filterAssets !== "area" || a.CanBeDefaultAsset)
		.map(
			(a: Asset) =>
				({
					id: a.AssetId,
					name: a.AssetName,
					parentId: a.ParentAssetId,
					isLeaf: a.IsLeaf,
					isActive: true,
				}) as TreeCode,
		);

	const assets = buildTreeAssets(rowsMapped || []);

	return (
		<FilterPaper>
			{/* One row, like every other list page: mode and navigator, search, asset */}
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					columnGap: 2,
					minHeight: NAVIGATOR_ROW_MIN_HEIGHT,
				}}
			>
				<NavigationModeSwitch mode={mode} onChange={onModeChange} />
				{/* Both stay mounted so each keeps its own position (a remount
				    would reset a Custom range) */}
				<Box sx={{ ...navigatorBox(mode === "shift"), flex: 2 }}>
					<ShiftDayNavigatorControl preventInit withPaper={false} />
				</Box>
				<Box sx={{ ...navigatorBox(mode === "period"), flex: 2 }}>
					<ShiftPeriodNavigatorControl
						key={periodNavigatorKey}
						onChange={onChangeShiftPeriod}
						value={shiftPeriod}
						actualPeriod={actualPeriod}
						showShiftAndCrews={false}
						dateFormat="MM/DD/YYYY"
						periodOptions={customPeriodOptions}
					/>
				</Box>
				<Box sx={{ flex: 1, minWidth: 200 }}>
					<TextField
						fullWidth
						label="Search"
						variant="outlined"
						size="small"
						value={quickFilter}
						inputRef={searchRef}
						onChange={(event) => {
							onQuickFilterChange(event?.target.value);
						}}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
							},
						}}
					/>
				</Box>
				{withAssetFilter === "tree" && assets && (
					<Box sx={{ flex: 1, minWidth: 220 }}>
						<TreePickerControlV2
							inputTitle="Asset"
							title="Choose Asset"
							dataSource={assets}
							value={assetId || 0}
							description=""
							onSelect={(value) => setAssetId(value ?? null)}
							selectBranch
							selectActive={false}
							selectInternal={false}
							showPath
							allowSearch
						/>
					</Box>
				)}
				{withAssetFilter === "selector" && (
					<Box sx={{ flex: 1, minWidth: 220 }}>
						<AssetsFilter
							value={assetId ?? null}
							onChange={(newValue) => {
								setAssetId(newValue ?? null);
							}}
							filterAreaAssets={filterAssets === "area"}
							isFilter
						/>
					</Box>
				)}
			</Box>
		</FilterPaper>
	);
};
